import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FlightIcon from "@mui/icons-material/Flight";
import PersonIcon from "@mui/icons-material/Person";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";

import { useFlightNoteHistory } from "../../hooks/useFlightNoteHistory";
import { getLastSeenReceivedAt, setLastSeenReceivedAt } from "./messageStorage";

const formatRoute = (departure?: string | null, arrival?: string | null) =>
  departure && arrival ? `${departure} → ${arrival}` : null;

const formatTimestamp = (value?: string | null) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) {
    return "—";
  }

  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isCrewMessage = (note: OpsNoteHistoryEntry) =>
  !note.senderUserId && !note.senderEmail;

type Props = {
  open: boolean;
  onClose: () => void;
  flights: OpsFlight[];
  isLoading?: boolean;
  onSeenUpdate?: () => void;
};

export const MessagePanel: FunctionComponent<Props> = ({
  open,
  onClose,
  flights,
  isLoading = false,
  onSeenUpdate,
}) => {
  const [activeFlightId, setActiveFlightId] = useState<string | null>(null);
  const [showCrewAlert, setShowCrewAlert] = useState(false);
  const latestCrewMessageRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      setActiveFlightId(null);
      setShowCrewAlert(false);
      latestCrewMessageRef.current = null;
      return;
    }

    if (!flights.length) {
      setActiveFlightId(null);
      return;
    }

    if (!activeFlightId || !flights.some((flight) => flight.flightID === activeFlightId)) {
      setActiveFlightId(flights[0].flightID);
    }
  }, [open, flights, activeFlightId]);

  const activeFlight = useMemo(
    () => flights.find((flight) => flight.flightID === activeFlightId) || null,
    [flights, activeFlightId]
  );

  const {
    data: noteHistory,
    isLoading: isHistoryLoading,
    refetch: refetchHistory,
  } = useFlightNoteHistory(activeFlight?.flightID || null, open);

  useEffect(() => {
    if (!open || !activeFlight?.flightID) {
      return;
    }

    const interval = window.setInterval(() => {
      void refetchHistory();
    }, 30000);

    return () => {
      window.clearInterval(interval);
    };
  }, [open, activeFlight?.flightID, refetchHistory]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const latestCrewMessage = noteHistory?.notes?.find(isCrewMessage);
    if (!latestCrewMessage?.createdAt) {
      return;
    }

    if (
      latestCrewMessageRef.current &&
      latestCrewMessageRef.current !== latestCrewMessage.createdAt
    ) {
      setShowCrewAlert(true);
    }

    latestCrewMessageRef.current = latestCrewMessage.createdAt;
  }, [noteHistory, open]);

  useEffect(() => {
    if (!open || !activeFlight?.flightID) {
      return;
    }

    const latestCrewMessage = noteHistory?.notes?.find(isCrewMessage);
    const latestTimestamp =
      latestCrewMessage?.createdAt ||
      activeFlight.noteStatus?.latestReceivedAt ||
      null;

    if (latestTimestamp) {
      setLastSeenReceivedAt(activeFlight.flightID, latestTimestamp);
      onSeenUpdate?.();
    }
  }, [open, activeFlight?.flightID, activeFlight?.noteStatus?.latestReceivedAt, noteHistory, onSeenUpdate]);

  const unreadFlightIds = useMemo(() => {
    const ids = new Set<string>();

    flights.forEach((flight) => {
      const latestReceived = flight.noteStatus?.latestReceivedAt || null;
      if (!latestReceived) {
        return;
      }

      const lastSeen = getLastSeenReceivedAt(flight.flightID);
      if (!lastSeen || new Date(latestReceived) > new Date(lastSeen)) {
        ids.add(flight.flightID);
      }
    });

    return ids;
  }, [flights]);

  const routeLabel = useMemo(
    () => formatRoute(activeFlight?.departureAirport, activeFlight?.arrivalAirport),
    [activeFlight?.departureAirport, activeFlight?.arrivalAirport]
  );

  const orderedNotes = useMemo(() => {
    if (!noteHistory?.notes?.length) {
      return [] as OpsNoteHistoryEntry[];
    }
    return [...noteHistory.notes].reverse();
  }, [noteHistory?.notes]);

  const handleTabChange = (_event: React.SyntheticEvent, value: string) => {
    setActiveFlightId(value);
    setShowCrewAlert(false);
    latestCrewMessageRef.current = null;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="flight-messages-title"
    >
      <DialogTitle id="flight-messages-title" sx={{ pr: 7 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <FlightIcon color="primary" />
          <Typography variant="h6">Flight messages</Typography>
        </Stack>
        <IconButton
          onClick={onClose}
          aria-label="Close flight messages"
          sx={{ position: "absolute", right: 16, top: 16 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {isLoading && (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        )}
        {!isLoading && flights.length === 0 && (
          <Box sx={{ p: 3 }}>
            <Alert severity="info">No flights are available for messaging today.</Alert>
          </Box>
        )}
        {!isLoading && flights.length > 0 && (
          <Box>
            <Tabs
              value={activeFlightId}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                px: 2,
                pt: 1,
              }}
            >
              {flights.map((flight) => {
                const hasUnread = unreadFlightIds.has(flight.flightID);
                const flightLabel =
                  flight.flightIdentifier || flight.flightID || "Flight";
                return (
                  <Tab
                    key={flight.flightID}
                    value={flight.flightID}
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2">{flightLabel}</Typography>
                        {flight.aircraftRegistration && (
                          <Typography variant="caption" color="text.secondary">
                            {flight.aircraftRegistration}
                          </Typography>
                        )}
                        {hasUnread && (
                          <Chip
                            size="small"
                            color="error"
                            label="New"
                            sx={{ height: 20 }}
                          />
                        )}
                      </Stack>
                    }
                  />
                );
              })}
            </Tabs>
            <Box sx={{ p: 3 }}>
              {activeFlight && (
                <Stack spacing={2}>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Flight details
                      </Typography>
                      <Stack spacing={0.5} sx={{ mt: 1 }}>
                        <Typography variant="body1" fontWeight={600}>
                          {activeFlight.flightIdentifier || activeFlight.flightID}
                        </Typography>
                        {routeLabel && (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <FlightIcon fontSize="small" color="action" />
                            <Typography variant="body2">{routeLabel}</Typography>
                          </Stack>
                        )}
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            Aircraft: {activeFlight.aircraftRegistration || activeFlight.pps?.tail || "—"}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Crew & timing
                      </Typography>
                      <Stack spacing={0.5} sx={{ mt: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <AccessTimeIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            STD: {formatTimestamp(activeFlight.scheduledDeparture)}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <AccessTimeIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            STA: {formatTimestamp(activeFlight.scheduledArrival)}
                          </Typography>
                        </Stack>
                        {noteHistory?.summary?.uniqueReceivers?.length ? (
                          <Typography variant="body2">
                            Crew TLC: {noteHistory.summary.uniqueReceivers.join(", ")}
                          </Typography>
                        ) : (
                          <Typography variant="body2">Crew TLC: —</Typography>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                  {showCrewAlert && (
                    <Alert
                      severity="info"
                      onClose={() => setShowCrewAlert(false)}
                    >
                      New crew message received for this flight.
                    </Alert>
                  )}
                  <Divider />
                  {isHistoryLoading && (
                    <Stack alignItems="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </Stack>
                  )}
                  {!isHistoryLoading && orderedNotes.length === 0 && (
                    <Alert severity="info">No messages have been sent for this flight yet.</Alert>
                  )}
                  {!isHistoryLoading && orderedNotes.length > 0 && (
                    <Stack spacing={2}>
                      {orderedNotes.map((note) => {
                        const isCrew = isCrewMessage(note);
                        const statusLabel = note.acknowledgedAt
                          ? "Acknowledged"
                          : note.receivedAt
                          ? "Received"
                          : note.status && note.status !== "sent"
                          ? "Queued"
                          : "Sent";
                        const sentAtLabel = note.deliveredAt || note.createdAt;
                        return (
                          <Box
                            key={note.id}
                            sx={{
                              alignSelf: isCrew ? "flex-start" : "flex-end",
                              bgcolor: isCrew ? "grey.100" : "primary.light",
                              color: isCrew ? "text.primary" : "primary.contrastText",
                              borderRadius: 3,
                              p: 2,
                              maxWidth: "85%",
                              boxShadow: 1,
                            }}
                          >
                            <Stack spacing={1}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="subtitle2">
                                  {note.title || (isCrew ? "Crew update" : "Ops message")}
                                </Typography>
                                {note.noteType && (
                                  <Chip size="small" label={note.noteType} sx={{ height: 20 }} />
                                )}
                                <Chip size="small" label={statusLabel} sx={{ height: 20 }} />
                              </Stack>
                              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                {note.message}
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Typography variant="caption">
                                  {isCrew
                                    ? `Crew: ${note.receiverTlc || note.recipientTlc || "Unknown"}`
                                    : `Ops: ${note.senderEmail || "Unknown"}`}
                                </Typography>
                                <Typography variant="caption">•</Typography>
                                <Typography variant="caption">
                                  {statusLabel}: {formatTimestamp(sentAtLabel)}
                                </Typography>
                                {note.acknowledgedAt && (
                                  <>
                                    <Typography variant="caption">•</Typography>
                                    <Typography variant="caption">
                                      Ack: {formatTimestamp(note.acknowledgedAt)}
                                    </Typography>
                                  </>
                                )}
                              </Stack>
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </Stack>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
